import {_} from 'lodash';
import * as ko from "knockout";
import {ISelection} from "./index";
import {global} from "../../../appContext";
import {ICondition, IMessageData, IObjectData, IRule, ISystem, ISystemRole} from "../../../index";
import {ErrorTracker} from "../../../error.tracker";
import {_d, getSyncFieldValue} from "../../../util/misc";
import {nsRuleset} from "../nsRuleset";
import {MutableRuleAbstract} from "../mutable/rule/MutableRuleAbstract";
import {AvailableData} from "../mutable/rule/AvailableData";
import {validateServerDate} from "../../../util/datetime";
import {ICustomSystemAccess} from "../../combobox/system-role";
import {ImportFormatId} from "../../../constants/ImportFormats";
import RULE_DEFAULT = nsRuleset.RULE_DEFAULT;
import validateRange = nsRuleset.validateRange;

export class RuleMutable extends MutableRuleAbstract {
    protected readonly et: ErrorTracker = global.errorAreas.get("rules.wizard");
    protected readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private lastSelection: ISelection | null = null;

    public readonly departments: KnockoutObservable<any | null> = ko.observable(null);
    public readonly permission: KnockoutObservableArray<ICustomSystemAccess> = ko.observableArray<ICustomSystemAccess>([]);

    public readonly isActive: KnockoutObservable<boolean> = ko.observable(true);

    constructor(availableData: AvailableData, public selection: KnockoutObservable<ISelection | null>) {
        super(availableData);
        this.kosub.subscribe(this.selection, this.setSelection.bind(this));
    }

    private setSelection(selection: ISelection | null) {
        if (!selection || this.lastSelection == selection) {
            return
        }
        this.lastSelection = selection;

        const rule = _.defaults(selection.definition?.rule, RULE_DEFAULT);
        this.argument(rule.argument);
        this.argumentoperator(rule.argumentoperator);
        this.attribute(rule.attribute);
    }

    public isValid(): boolean {
        const bools: boolean[] = [];
        if (!this.isActive()) {
            return true;
        }
        switch (this.selection()?.definition?.name) {
            case 'pick_attribute_and_value': {
                const conditionType = this.availableData.getArgumentType(this.argumentoperator() ?? '');
                let boolA = !!this.attribute() || conditionType == 'ruleset';
                let boolB = !!this.argumentoperator() && !!this.argumentoperator();
                let boolC = this.argument() !== "";
                switch (conditionType) {
                    case "array":
                    case "string":
                    case "ruleset":
                        break;
                    case "datetime":
                        boolC = validateRange(String(this.argument())) || validateServerDate(String(this.argument()));
                        break;
                    case "int":
                        break;
                    case "bool":
                        boolC = this.argument() === ""; // is always == ""
                        break;
                }
                this.et.update(!boolA, this.attribute, "invalid attribute");
                this.et.update(!boolB, this.argumentoperator, "invalid condition");
                this.et.update(!boolC, this.argument, "invalid argument");
                bools.push(boolA, boolB, boolC);
                break;
            }
            case 'pick_ruleset': {
                const b = !!this.argument();
                this.et.update(!b, this.argument, `invalid ruleset`);
                bools.push(b);
                break;
            }
            case 'pick_departments': {
                const b = !!this.departments()?.length;
                this.et.update(!b, this.argument, 'none selected');
                bools.push(b);
                break;
            }
            case 'event':
                break;
            case 'pick_system_permission': {
                const b = !!this.permission();
                this.et.update(!b, this.permission, 'invalid s|s:((access control|ac))');
                bools.push(b);
                break;
            }
        }
        return bools.every(Boolean);
    }

    public getRules(): Partial<IRule>[] {
        if (!this.isActive()) {
            return [];
        }
        const ret: Partial<IRule>[] = [];
        const def = this.selection()?.definition;
        const rules = def?.rules ?? [];
        const _rules = this.getDefinitionRule()
        if (_rules == null) {
            // no rule for this selection type
        } else if (Array.isArray(_rules)) {
            ret.push(..._rules)
        } else {
            ret.push(_rules)
        }
        return ret.concat(rules).filter(Boolean);
    }

    protected getDefinitionRule(): Partial<IRule> | Partial<IRule>[] | undefined {
        const def = this.selection()?.definition;
        const rule = def?.rule ?? {};
        switch (def?.name) {
            case 'pick_attribute_and_value': {
                return _.defaults({
                    argument: this.argument(),
                    attribute: this.attribute(),
                    argumentoperator: this.argumentoperator(),
                }, rule);
            }
            case 'pick_ruleset': {
                return _.defaults({
                    argument: this.argument(),
                }, rule);
            }
            case 'pick_departments': {
                const importFormatId = global.login.selectedOrganization()?.importformat?.id
                const syncfieldName = def.syncfieldName ?? (importFormatId == ImportFormatId.Visma_HR8 ? 'DepartmentNumber': 'OrgUnitnr');
                const departments = this.departments() ?? []
                if (def.subDepartments) {
                    return _.defaults({
                        argument: departments
                            .map((o: IObjectData) => o.id)
                            .map((id: number) => `[DESCENDANTS;${id};${syncfieldName};DepartmentNumber;ParentDepartmentNumber]`)
                            .join(','),
                    }, rule);
                } else {
                    return _.defaults({
                        argument: departments.map((o: IObjectData) => getSyncFieldValue(syncfieldName, o.syncfields)).join(','),
                    }, rule);
                }
            }
            case 'pick_system_permission': {
                return this.permission()
                    .map(o => {
                        let type: 'SYSTEMROLE' | 'SYSTEM' | undefined
                        let id: ISystem['id'] | ISystemRole['id'] | undefined
                        if (o.role) {
                            type = 'SYSTEMROLE'
                            id = o.role.id
                        } else if (o.system) {
                            type = 'SYSTEM'
                            id = o.system.id
                        } else {
                            _d.error('Neither role nor system.')
                            return undefined
                        }
                        return _.defaults({
                            attribute: `[OBJECTPERMISSION.${type};Name;Id;${id}]`,
                        }, rule)
                    })
                    .filter((rule): rule is Partial<IRule> => !!rule)
            }
            default:
                return undefined
        }
    }

    public conditionFilter(condition: ICondition): boolean {
        return ['string', 'bool', 'int'].includes(condition.type)
    }

    override dispose() {
        this.kosub.unsuscribeAll();
    }

}
